import PropTypes from 'prop-types';
import { formatMoneyWithSign } from 'utils/currencyHelper';
import Presenter from 'utils/PropTypesPresenter';

const SECOND_PERSON_DEFAULT_FIELD_VALUES = [
  { field: 'secondIncomeValue', value: 75000 },
  { field: 'secondCarPaymentValue', value: 250 },
  { field: 'secondStudentPaymentValue', value: 75 },
  { field: 'secondCardPaymentValue', value: 50 },
  { field: 'secondOtherPaymentValue', value: 0 },
  { field: 'secondSavingsValue', value: 45000 },
];

const HOUSE_FIELD_VALUES = [
  { field: 'currentHouseValue', value: 250000 },
  { field: 'currentHouseMortgageBal', value: 200000 },
];

const TAX_RATE = 6 / 100;
const DEFAULT_MORTGAGE_BALANCE_RATIO = 80 / 100;
const CAR_PAYMENT_RATIO = 4 / 100;
const STUDENT_PAYMENT_RATIO = 1 / 100;
const CARD_PAYMENT_RATIO = 1 / 100;
const SAVINGS_RATIO = 60 / 100;

export default new Presenter(
  {
    currentHouse: PropTypes.bool,
    currentHouseValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currentHouseMortgageBal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    hasSecondPerson: PropTypes.bool,
    secondIncomeValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    secondCarPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    secondStudentPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    secondCardPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    secondOtherPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    secondSavingsValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    incomeValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    carPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    studentPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    cardPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    otherPaymentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    savingsValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    dtiLevel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    monthsCushion: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    interestRate: PropTypes.number,
    mortgageTerm: PropTypes.number,
    propertyTaxRate: PropTypes.number,
  },
  {
    prepareFieldValues(field, value, manuallyChangedFields = {}) {
      if (this.isCurrentHouseField(field)) {
        const houseFieldValues = value
          ? HOUSE_FIELD_VALUES
          : HOUSE_FIELD_VALUES.map(({ field: houseField }) => ({
              field: houseField,
              value: 0,
            }));
        return [{ field, value }, ...houseFieldValues];
      }
      if (this.isSecondPersonField(field)) {
        const secondPersonDefaultFieldValues = value
          ? SECOND_PERSON_DEFAULT_FIELD_VALUES
          : SECOND_PERSON_DEFAULT_FIELD_VALUES.map(({ field: secondPersonField }) => ({
              field: secondPersonField,
              value: 0,
            }));
        return [{ field, value }, ...secondPersonDefaultFieldValues];
      }
      if (this.isIncomeValueField(field)) {
        const additionalFieldsValues = [
          { field: 'carPaymentValue', value: this.calculatedCarPayment(value) },
          { field: 'studentPaymentValue', value: this.calculatedStudentPayment(value) },
          { field: 'cardPaymentValue', value: this.calculatedCardPayment(value) },
          { field: 'savingsValue', value: this.calculatedSavings(value) },
        ];
        const filteredByManualChangedFields = additionalFieldsValues.filter(
          fieldValue => !manuallyChangedFields[fieldValue.field],
        );
        return [{ field, value }, ...filteredByManualChangedFields];
      }
      if (this.isSecondIncomeValueField(field)) {
        const additionalFieldsValues = [
          { field: 'secondCarPaymentValue', value: this.calculatedCarPayment(value) },
          { field: 'secondStudentPaymentValue', value: this.calculatedStudentPayment(value) },
          { field: 'secondCardPaymentValue', value: this.calculatedCardPayment(value) },
          { field: 'secondSavingsValue', value: this.calculatedSavings(value) },
        ];
        const filteredByManualChangedFields = additionalFieldsValues.filter(
          fieldValue => !manuallyChangedFields[fieldValue.field],
        );
        return [{ field, value }, ...filteredByManualChangedFields];
      }
      return [{ field, value }];
    },
    isCurrentHouseField(field) {
      return field === 'currentHouse';
    },
    isIncomeValueField(field) {
      return field === 'incomeValue';
    },
    isSecondIncomeValueField(field) {
      return field === 'secondIncomeValue';
    },
    isSecondPersonField(field) {
      return field === 'hasSecondPerson';
    },
    readableTotalDisplay(values) {
      const totalDisplay = this.currentHouseValue(values) - this.currentHouseMortgageBal(values);
      return formatMoneyWithSign(totalDisplay, 'USD');
    },
    readableAgentCommision(values) {
      const agentCommision = this.currentHouseValue(values) * TAX_RATE;
      return formatMoneyWithSign(agentCommision, 'USD');
    },
    readableAdjustedEquity(values) {
      const agentCommision = this.currentHouseValue(values) * TAX_RATE;
      const adjustedEquity = this.currentHouseValue(values) - agentCommision - this.currentHouseMortgageBal(values);
      return formatMoneyWithSign(adjustedEquity, 'USD');
    },
    calculateDefaultMortgageBalance(values) {
      return this.currentHouseValue(values) * DEFAULT_MORTGAGE_BALANCE_RATIO;
    },
    calculatedCarPayment(incomeValue) {
      return Math.round((incomeValue / 12) * CAR_PAYMENT_RATIO);
    },
    calculatedStudentPayment(incomeValue) {
      return Math.round((incomeValue / 12) * STUDENT_PAYMENT_RATIO);
    },
    calculatedCardPayment(incomeValue) {
      return Math.round((incomeValue / 12) * CARD_PAYMENT_RATIO);
    },
    calculatedSavings(incomeValue) {
      return Math.round(incomeValue * SAVINGS_RATIO);
    },
    readableDtiLevel(values) {
      return `${(this.dtiLevel(values) * 100).toFixed(0)}%`;
    },
    formattedMoney(value) {
      if (Number.isNaN(value)) return value;
      return formatMoneyWithSign(value, 'USD');
    },
  },
);



// WEBPACK FOOTER //
// ./src/presenters/QuizPresenter.js